import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, ChevronUp, ChevronDown, Minus, MessageSquare, Star, Clock } from "lucide-react";
import { useEntries, type StaffMemberMapped } from "@/hooks/use-entries";
import { useSubscription } from "@/hooks/use-subscription";
import { useAuth } from "@/contexts/auth-context";
import trampolineBg from "@/assets/trampoline-bg.jpg";
import { useLeaderboard } from "@/hooks/use-leaderboard-mutations";
import { useVenue } from "@/hooks/use-venue";

const ROTATION_INTERVAL = 18000; // 18s per screen

const TVDisplay = () => {
  const navigate = useNavigate();
  const { id: boardId } = useParams();
  const { user } = useAuth();
  const { data: entries = [], isLoading } = useEntries(user?.venueId, boardId);
  const { data: leaderboard } = useLeaderboard(user?.venueId, boardId ?? null);
  const { data: venue } = useVenue(user?.venueId);
  const { data: subscription, isLoading: subscriptionLoading, error: subscriptionError } = useSubscription(user?.venueId ?? null);
  const [activeScreen, setActiveScreen] = useState(0);
  const [countdown, setCountdown] = useState({ days: 16, hours: 8, minutes: 42, seconds: 15 });

  const screens = ["leaderboard", "rewards", "message", "hallOfFame"];

  // Rotate screens
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveScreen((prev) => (prev + 1) % screens.length);
    }, ROTATION_INTERVAL);
    return () => clearInterval(timer);
  }, []);

  // Countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        let { days, hours, minutes, seconds } = prev;
        seconds--;
        if (seconds < 0) { seconds = 59; minutes--; }
        if (minutes < 0) { minutes = 59; hours--; }
        if (hours < 0) { hours = 23; days--; }
        if (days < 0) return prev;
        return { days, hours, minutes, seconds };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const countdownStr = countdown.days > 0
    ? `${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`
    : countdown.hours > 0
    ? `${countdown.hours}h ${countdown.minutes}m ${countdown.seconds}s`
    : `${countdown.minutes}m ${countdown.seconds}s`;

  // Calculate subscription allowed status
  const subscriptionAllowed = subscription
    ? subscription.status === "active" ||
      subscription.status === "trialing" ||
      (subscription.status === "past_due" &&
        subscription.gracePeriodEnd &&
        new Date(subscription.gracePeriodEnd) > new Date())
    : true; // Fail open if no subscription data

  // Fail open: if loading or error, show leaderboard. Only block if subscription is explicitly denied.
  const subscriptionDenied = !subscriptionLoading && !subscriptionError && !subscriptionAllowed;

  if (subscriptionDenied) {
    return (
      <div className="flex h-screen w-screen items-center justify-center select-none cursor-default" style={{ background: "#0A1628" }}>
        <div className="flex flex-col items-center justify-center text-center px-8 max-w-lg">
          <h1 className="text-4xl font-extrabold text-white mb-4 lg:text-5xl">Subscription Required</h1>
          <p className="text-lg text-gray-300 mb-6 lg:text-xl">This leaderboard display requires an active subscription.</p>
          <p className="text-sm text-gray-400 lg:text-base">Contact your venue administrator to reactivate.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-screen w-screen flex-col overflow-hidden select-none cursor-default bg-black">
      {/* Exit Demo button */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-4 right-4 z-50 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white/70 backdrop-blur-md transition-colors hover:bg-white/20 hover:text-white"
      >
        Exit Demo
      </button>
      {/* Faded trampoline background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: `url(${trampolineBg})` }}
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />

      {/* Content layer */}
      <div className="relative z-10 flex h-full flex-col">
        {/* Header */}
        <header className="flex items-center justify-between px-8 py-5">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-white lg:text-4xl">
                {leaderboard?.name ?? "Sales Leaderboard"}
              </h1>
              {venue?.name && (
                <p className="mt-1 text-sm text-gray-400 lg:text-base">{venue.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-neon-orange">
              <Clock className="h-5 w-5 animate-pulse-neon" />
              <span className="text-lg font-bold neon-glow-orange lg:text-2xl">{countdownStr}</span>
            </div>
          </div>
        </header>

        {/* Progress dots */}
        <div className="flex justify-center gap-2 pb-3">
          {screens.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === activeScreen ? "w-8 bg-neon-orange" : "w-1.5 bg-gray-600"
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden px-8 pb-6">
          <AnimatePresence mode="wait">
            {activeScreen === 0 && <LeaderboardScreen key="lb" data={entries} isLoading={isLoading} />}
            {activeScreen === 1 && <RewardsScreen key="rw" leaderboard={leaderboard as ({ config?: Record<string, unknown> } | null)} />}
            {activeScreen === 2 && <MessageScreen key="msg" leaderboard={leaderboard as ({ config?: Record<string, unknown> } | null)} />}
            {activeScreen === 3 && <HallOfFameScreen key="hof" entries={entries} />}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

const LeaderboardScreen = ({ data, isLoading }: { data: StaffMemberMapped[]; isLoading?: boolean }) => {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.5 }}
        className="flex h-full items-center justify-center"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-orange" />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="flex h-full flex-col"
    >
      {/* Header row */}
      <div className="mb-3 flex items-center px-4 text-xs font-semibold uppercase tracking-wider text-gray-500 lg:text-sm">
        <span className="w-16 text-center">Rank</span>
        <span className="w-14" />
        <span className="flex-1">Name</span>
        <span className="w-24 text-center">Sold</span>
        <span className="w-28 text-center">Value</span>
        <span className="w-12" />
      </div>
      <div className="flex-1 space-y-2 overflow-hidden">
        {data.map((s, i) => (
          <motion.div
            key={s.id}
            layout
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={`flex items-center rounded-xl px-4 py-3 lg:py-4 ${
              i === 0
                ? "bg-gradient-to-r from-neon-orange/20 to-transparent border border-neon-orange/30"
                : i < 3
                ? "bg-tv-surface/80 border border-tv-surface"
                : "bg-tv-surface/50"
            }`}
          >
            <span className={`w-16 text-center text-2xl font-extrabold lg:text-3xl ${
              i === 0 ? "text-neon-orange" : i < 3 ? "text-white" : "text-gray-500"
            }`}>
              {s.rank}
            </span>
            <img src={s.avatar} alt={s.name} className="h-10 w-10 rounded-full object-cover lg:h-12 lg:w-12" />
            <span className="ml-4 flex-1 text-lg font-semibold text-tv-text lg:text-xl">{s.name}</span>
            <span className="w-24 text-center text-xl font-bold text-neon-orange lg:text-2xl neon-glow-orange">
              {s.membershipsSold}
            </span>
            <span className="w-28 text-center text-base text-tv-muted lg:text-lg">
              ${s.valueSold.toLocaleString()}
            </span>
            <span className="flex w-12 items-center justify-center">
              {s.movement > 0 ? (
                <span className="flex items-center gap-0.5 text-neon-green text-sm font-bold">
                  <ChevronUp className="h-5 w-5" />{s.movement}
                </span>
              ) : s.movement < 0 ? (
                <span className="flex items-center gap-0.5 text-neon-orange text-sm font-bold">
                  <ChevronDown className="h-5 w-5" />{Math.abs(s.movement)}
                </span>
              ) : (
                <Minus className="h-4 w-4 text-tv-muted" />
              )}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const RewardsScreen = ({ leaderboard }: { leaderboard?: { config?: Record<string, unknown> } | null }) => {
  const prizes = (leaderboard?.config?.prizes as Array<{place: number; title: string; description: string}>) ?? [];

  if (prizes.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.5 }}
        className="flex h-full flex-col items-center justify-center"
      >
        <Trophy className="mb-6 h-16 w-16 text-neon-orange lg:h-20 lg:w-20" />
        <h2 className="text-3xl font-extrabold text-tv-text neon-glow-orange lg:text-5xl">What's Up For Grabs</h2>
        <p className="mt-6 text-lg text-tv-muted">Prizes will be announced soon. Stay tuned!</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5 }}
      className="flex h-full flex-col items-center justify-center"
    >
      <Trophy className="mb-6 h-16 w-16 text-neon-orange lg:h-20 lg:w-20" />
      <h2 className="text-3xl font-extrabold text-tv-text neon-glow-orange lg:text-5xl">What's Up For Grabs</h2>
      <div className="mt-10 w-full max-w-2xl space-y-5">
        {prizes.map((r, i) => (
          <motion.div
            key={r.place}
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.15 }}
            className="flex items-center gap-6 rounded-2xl bg-tv-surface px-8 py-5"
          >
            <span className="text-4xl">{["🥇", "🥈", "🥉"][i]}</span>
            <div>
              <p className="text-2xl font-bold text-tv-text lg:text-3xl">{r.title}</p>
              {r.description && <p className="text-sm text-tv-muted">{r.description}</p>}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const MessageScreen = ({ leaderboard }: { leaderboard?: { config?: Record<string, unknown> } | null }) => {
  const msg = leaderboard?.config?.managerMessage as {text: string; managerName: string; title: string} | undefined;

  if (!msg?.text) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex h-full flex-col items-center justify-center text-center"
    >
      <MessageSquare className="mb-6 h-12 w-12 text-neon-orange lg:h-16 lg:w-16" />
      <p className="max-w-2xl text-2xl font-medium leading-relaxed text-tv-text lg:text-4xl">
        "{msg.text}"
      </p>
      <p className="mt-8 text-lg text-tv-muted">
        — {msg.managerName || "Manager"}{msg.title ? `, ${msg.title}` : ""}
      </p>
    </motion.div>
  );
};

const HallOfFameScreen = ({ entries }: { entries: StaffMemberMapped[] }) => {
  const topEntries = entries.slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="flex h-full flex-col items-center justify-center"
    >
      <Star className="mb-4 h-12 w-12 text-neon-orange lg:h-16 lg:w-16" />
      <h2 className="text-3xl font-extrabold text-white neon-glow-orange lg:text-5xl">Hall of Fame</h2>
      <p className="mt-2 text-lg text-tv-muted">Top performers this campaign.</p>
      {topEntries.length === 0 ? (
        <p className="mt-10 text-tv-muted">No entries yet — be the first on the board!</p>
      ) : (
        <div className="mt-10 grid w-full max-w-3xl grid-cols-3 gap-6">
          {topEntries.map((entry, i) => (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 + i * 0.1 }}
              className="flex flex-col items-center rounded-xl bg-tv-surface p-4"
            >
              <div className="mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-neon-orange/20 text-2xl font-bold text-neon-orange lg:h-16 lg:w-16">
                {["🥇", "🥈", "🥉"][i]}
              </div>
              <p className="text-sm font-bold text-tv-text">{entry.name}</p>
              <p className="mt-0.5 text-[11px] text-tv-muted">{entry.membershipsSold} sold</p>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default TVDisplay;
